import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation} from '@angular/core';
import {ComponentDefinition} from '@formbird/types';
import {ChangedDocumentService} from 'formbird-services';
import {FormbirdInjectorService} from '@services/injector/formbird-injector.service';
import * as moment from 'moment';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {MatTooltip} from '@angular/material/tooltip';
import {SclUtilValidationService} from '../../../../formbird-sc-shared/src/lib/scl-util-validation.service';


interface ApproveComponentDefinition extends ComponentDefinition {
  approveMessage?: any;
  keyIds?: any;
}

@Component({
  templateUrl: './approve.component.html',
  styleUrls: ['./approve.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApproveComponent extends AbstractComponent<ApproveComponentDefinition> implements OnInit {

  @Input() floatLabel: any;

  hideRequired: boolean;
  isRequired: boolean;
  isChecked: boolean;
  user: any;
  userAccountControlDocument: any;
  approveMessage: string;
  hasAccess: boolean;

  constructor(
    protected cdr: ChangeDetectorRef,
    protected changedDocumentService: ChangedDocumentService,
    protected formbirdInjectorService: FormbirdInjectorService,
    protected setupService: ComponentSetupService,
    private sclUtilValidationService: SclUtilValidationService,
  ) {
    super(formbirdInjectorService, changedDocumentService, setupService, cdr);

    const loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
    this.user = loggedInUserService.user.account;
    this.userAccountControlDocument = loggedInUserService.user.accountControlDocument;
  }

  ngOnInit() {

    this.shouldNotifyCompInitializedInSuper = false;

    super.ngOnInit();

    this.isRequired = this.componentDefinition.mandatory ?? false;
    this.hideRequired = !this.isRequired;

    this.isChecked = !!(this.fieldValue && this.fieldValue.approveAccount && this.fieldValue.approveDate);
    this.approveMessage = this.componentDefinition.approveMessage ?? 'Approved by:';
    this.ensureAccess();

    this.cdr.detectChanges();

    const self = this;
    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(tplItem => {
      self.onComponentDefChanged(tplItem);
      self.ensureAccess();
      self.cdr.detectChanges();
    });
    this.subs.add(compSub);
  }

  ensureAccess() {
    this.hasAccess = this.sclUtilValidationService.checkAccess(this.componentDefinition.keyIds, this.userAccountControlDocument, 'Update');
    if (!this.hasAccess) {
      this.formControl.disable();
    }
  }

  onFieldValueChanged(fieldValue) {
    super.onFieldValueChanged(fieldValue, false);
    this.isChecked = !!(this.fieldValue);
    this.cdr.detectChanges()
  }

  onControlValueChanged(value) {
    // only update value if we have access
    if (!(typeof value === 'undefined' || value === null) && this.hasAccess) {
      this.isChecked = !!value;
      this.handleApproveAction();
    }
  }

  // we use a getter instead of a property because we want it to update on each render of the component.
  // this.approveMessage may be undefined and if we set the message then, it'll result in "undefined" being displayed
  get approveUserMsg() {
    return this.isChecked && this.fieldValue ?
      `${this.approveMessage} ${this.fieldValue.approveAccount.name}, ${this.formatTime(this.fieldValue.approveDate)}` :
      '';
  }

  handleApproveAction() {
    // if we reach this point, we are sure to have access so no need to also check it here
    if (this.disabled) {
      return;
    }

    const dataUpdated = {
      approveAccount: {
        documentId: null,
        name: null
      },
      approveDate: null
    };

    if (this.isChecked === true) {
      dataUpdated.approveAccount.documentId = this.user.documentId;
      dataUpdated.approveAccount.name = this.user.systemHeader.summaryName ?
        this.user.systemHeader.summaryName : this.user.email;
      dataUpdated.approveDate = new Date();
      this.fieldValue = dataUpdated;
    } else {
      this.fieldValue = undefined;
    }

    this.valueChanged(this.fieldValue);
    this.detectChanges();
  }

  formatTime(time) {
    const longDate = 'MMM DD YYYY, h:mm:ss a';
    return moment(time).fromNow() + ' (' + moment(time).format(longDate) + ')';
  }

  showToolTip(tooltip: MatTooltip, container: HTMLDivElement, labelContainer: HTMLElement) {
    return (labelContainer.offsetWidth <= container.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }
}
